import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Tooltip, useTheme } from '@mui/material';
import Player from '../models/player';
import { colors } from '../theme/colors';

interface IMyProps {
  PlayerData: Player[];
  Pagination: number[];
  SetPlanning: boolean;
  LastSet?: number;
  onPlayerDoubleClick?: (player: Player) => void;
}

const PlayersTable: React.FC<IMyProps> = (props: IMyProps) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(props.Pagination[0]);
  const [dense] = React.useState(false);

  const theme = useTheme();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, props.Pagination[0]));
    setPage(0);
  };

  function getPlayerColor(team: string): string {
    if (team === 'm') {
      return theme.palette.mode === 'dark' ? colors.defaultBlueDark : colors.defaultBlueLight;
    } else {
      return theme.palette.mode === 'dark' ? colors.defaultRedDark : colors.defaultRedLight;
    }
  }

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.PlayerData.length) : 0;

  const visibleRows = React.useMemo(
    () => [...props.PlayerData].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, props.PlayerData],
  );

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: props.SetPlanning ? 250 : 350,
        maxWidth: props.SetPlanning ? 400 : 450,
        padding: { xs: 1, md: 1.5, xl: 2 },
        borderRadius: 2,
        border: theme.palette.mode === 'dark' ? colors.gridBorderDark : colors.gridBorderLight,
        bgcolor:
          theme.palette.mode === 'dark' ? colors.gridBackgroundDark : colors.gridBackgroundLight,
      }}
    >
      <Paper sx={{ width: '100%', mb: 0 }}>
        <TableContainer component={Paper}>
          <Table
            aria-label="simple table"
            sx={{ '& td, & th': { padding: 1.25 } }}
            size="small"
            data-testid="players-table"
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    flex: 2,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  Meno
                </TableCell>
                {props.SetPlanning ? (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', flex: 1 }} align="center">
                      Nehral/a
                    </TableCell>
                    <Tooltip title="Za celý turnaj">
                      <TableCell sx={{ fontWeight: 'bold', flex: 1 }} align="center">
                        Hral/a
                      </TableCell>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', flex: 1 }} align="center">
                      Sety hrané
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', flex: 1 }} align="center">
                      Sety vyhrané
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', flex: 1 }} align="center">
                      Úspešnosť
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => {
                return (
                  <TableRow
                    sx={{
                      backgroundColor: getPlayerColor(row.team ?? 'unknown'),
                    }}
                    key={row.id}
                    onDoubleClick={() => {
                      if (props.onPlayerDoubleClick) props.onPlayerDoubleClick(row);
                    }}
                    style={{ cursor: props.SetPlanning ? 'pointer' : 'default' }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        flex: 2,
                        whiteSpace: { md: 'nowrap' },
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {row.lastname + ' ' + row.name}
                    </TableCell>
                    {props.SetPlanning ? (
                      <>
                        <TableCell align="center" sx={{ flex: 1 }}>
                          {props.LastSet && row.lastSet ? (
                            <>{props.LastSet - row.lastSet}</>
                          ) : (
                            <></>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ flex: 1 }}>
                          {row.setsPlayed}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="center" sx={{ flex: 1 }}>
                          {row.setsPlayed}
                        </TableCell>
                        <TableCell align="center" sx={{ flex: 1 }}>
                          {row.setsWon}
                        </TableCell>
                        <TableCell align="center" sx={{ flex: 1 }}>
                          {row.ratio !== null && row.ratio !== undefined
                            ? `${(row.ratio * 100).toFixed(2)} %`
                            : '-'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={props.Pagination}
          component="div"
          count={props.PlayerData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Počet záznamov:"
          sx={{
            '& .MuiTablePagination-toolbar': {
              padding: { xs: '0 8px', sm: '0 16px' },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              marginRight: { xs: 0, md: 0 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
            '& .MuiTablePagination-input': {
              marginRight: { xs: 2, md: props.SetPlanning ? 2 : 4 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
            '& .MuiTablePagination-select': {
              marginRight: { xs: 0, md: 0 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
            '& .MuiTablePagination-actions': {
              marginLeft: { xs: 0, md: props.SetPlanning ? 0 : 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default PlayersTable;
